import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { TOOL_BLURBS, type ToolId } from '@/lib/tool-content'
import { EDGE_LOCATION_COUNT } from '@/lib/check-locations'

export const Route = createFileRoute('/_site/tools/')({
  component: ToolsIndexPage,
  head: () => ({
    meta: [
      { title: 'Free Uptime Tools — Browser Check, SLA, Downtime' },
      {
        name: 'description',
        content:
          'Free website tools: SSL checker, DNS lookup, WHOIS and domain expiry checks, a real-browser check, a multi-region "is it down?" checker and uptime calculators.',
      },
      { property: 'og:title', content: 'Free Website & Uptime Tools — Uptrack' },
      {
        property: 'og:description',
        content: 'SSL, DNS and domain checks, browser checks, downtime checks and uptime/SLA calculators — free and instant.',
      },
      {
        property: 'og:image',
        content: 'https://uptrack.app/og/tools.png',
      },
    ],
    links: [{ rel: 'canonical', href: 'https://uptrack.app/tools' }],
  }),
})

const FEATURED = {
  to: '/tools/browser-check',
  title: 'Free Browser Check',
  description:
    'Load any URL in a real headless browser and see a screenshot plus Core Web Vitals (LCP, CLS, FCP, TTFB) — the way your users actually experience the page.',
  tag: 'New',
}

const NETWORK_TOOLS: ToolId[] = [
  'ssl-checker',
  'dns-lookup',
  'mx-lookup',
  'txt-record-lookup',
  'dns-propagation-checker',
  'domain-expiry-checker',
  'whois-lookup',
  'domain-age-checker',
]

const TOOLS = [
  {
    to: '/tools/is-website-down',
    title: 'Is This Website Down?',
    description: `Check if a site is reachable from ${EDGE_LOCATION_COUNT} locations on six continents at once.`,
  },
  {
    to: '/tools/uptime-calculator',
    title: 'Uptime Calculator',
    description: 'Turn an uptime percentage into real allowed downtime per day, week, month, and year.',
  },
  {
    to: '/tools/sla-calculator',
    title: 'SLA Calculator',
    description: 'Allowed downtime and error budget for any SLA target, across monthly and yearly windows.',
  },
  {
    to: '/tools/downtime-cost-calculator',
    title: 'Downtime Cost Calculator',
    description: 'Estimate what an outage costs your business from revenue and downtime duration.',
  },
] as const

function ToolsIndexPage() {
  return (
    <section className="container mx-auto px-4 py-12 md:py-16">
      <div className="max-w-3xl">
        <h1 className="text-[2.25rem] font-semibold leading-[1.1] tracking-tight md:text-[3rem]">Free website &amp; uptime tools</h1>
        <p className="mt-4 max-w-2xl text-lead text-body">
          No signup needed. Check an SSL certificate, DNS records or a domain, run a real browser check, see if a site is down
          from {EDGE_LOCATION_COUNT} locations, or work out downtime and SLA budgets.
        </p>
      </div>

      {/* Featured: browser check */}
      <Link
        to={FEATURED.to}
        className="group mt-10 flex flex-col gap-4 rounded-lg border border-border bg-card p-6 shadow-raised transition-colors hover:border-primary sm:flex-row sm:items-center sm:justify-between md:p-8"
      >
        <div>
          <div className="flex items-baseline gap-3">
            <h2 className="text-xl font-semibold text-foreground">{FEATURED.title}</h2>
            <span className="text-sm font-medium text-primary">{FEATURED.tag}</span>
          </div>
          <p className="mt-2 max-w-xl text-[15px] text-body">{FEATURED.description}</p>
        </div>
        <span className="inline-flex shrink-0 items-center font-medium text-primary">
          Try it <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
        </span>
      </Link>

      <h2 className="mt-12 text-xl font-semibold tracking-tight">SSL, DNS and domain tools</h2>
      <p className="mt-2 max-w-2xl text-body">
        Look up a certificate, a DNS record or a registration. Results come live from our servers, from public resolvers and from the registry.
      </p>
      <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
        {NETWORK_TOOLS.map((id) => (
          <Link key={id} to={`/tools/${id}`} className="group block rounded-lg border border-border bg-card p-6 transition-colors hover:border-primary">
            <h3 className="font-semibold text-foreground">{TOOL_BLURBS[id].name}</h3>
            <p className="mt-2 text-[15px] text-body">{TOOL_BLURBS[id].blurb}</p>
          </Link>
        ))}
      </div>

      <h2 className="mt-12 text-xl font-semibold tracking-tight">Uptime and website tools</h2>
      <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
        {TOOLS.map((t) => (
          <Link key={t.to} to={t.to} className="group block rounded-lg border border-border bg-card p-6 transition-colors hover:border-primary">
            <h3 className="font-semibold text-foreground">{t.title}</h3>
            <p className="mt-2 text-[15px] text-body">{t.description}</p>
          </Link>
        ))}
      </div>
    </section>
  )
}
